<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { renderMarkdown } from '@/utils/markdown'
import { decisionLabel, confidenceLabel, dataQualityLabel, phaseLabel, marketLabel } from '@/utils/labels'
import {
  createPaperOrderFromDecision,
  createThesisFromAnalysis,
  type DecisionDashboard,
  type DebateResult,
  type TradeProposal,
  type RiskReview,
  type BuffettChecklistResult,
  type MungerInversionReport,
  type ReflectionItem,
  type RedLineOperator,
} from '@/api/client'
import DebateArenaPanel from '@/components/DebateArenaPanel.vue'
import RiskCommitteePanel from '@/components/RiskCommitteePanel.vue'
import TraderProposalPanel from '@/components/TraderProposalPanel.vue'
import BuffettChecklistPanel from '@/components/BuffettChecklistPanel.vue'
import MungerInversionPanel from '@/components/MungerInversionPanel.vue'
import ValuationBandPanel from '@/components/ValuationBandPanel.vue'

const props = defineProps<{
  report: any
}>()

const decision = computed<DecisionDashboard | null>(() => {
  const d = props.report?.decision
  if (!d) return null
  if (d.decision_type && d.sniper_levels) return d as DecisionDashboard
  return null
})

const intent = computed(() => props.report?.intent || {})

// ===== ALENS-194: 结构化面板数据 (analysis 文档顶层字段) =====
const debateResult = computed<DebateResult | null>(() => props.report?.debate_result || null)
const riskReviews = computed<RiskReview[]>(() =>
  Array.isArray(props.report?.risk_reviews) ? props.report.risk_reviews : [],
)
const tradeProposal = computed<TradeProposal | null>(() => props.report?.trade_proposal || null)
const buffettChecklist = computed<BuffettChecklistResult | null>(
  () => props.report?.buffett_checklist || null,
)
const mungerInversion = computed<MungerInversionReport | null>(
  () => props.report?.munger_inversion || null,
)
const reflections = computed<ReflectionItem[]>(() =>
  Array.isArray(props.report?.reflections) ? props.report.reflections : [],
)

// 标签经 utils/labels.ts (i18n 感知), 与 History/Batch 口径一致
const actionMap = {
  buy: { label: `🟢 ${decisionLabel('buy')}`, color: 'var(--success)', bg: 'var(--bg-success-subtle)' },
  hold: { label: `🟡 ${decisionLabel('hold')}`, color: 'var(--warning)', bg: 'var(--bg-warning-subtle)' },
  sell: { label: `🔴 ${decisionLabel('sell')}`, color: 'var(--danger)', bg: 'var(--bg-danger-subtle)' }
}

const actionStyle = computed(() => {
  const a = decision.value?.decision_type || 'hold'
  return actionMap[a as keyof typeof actionMap] || actionMap.hold
})

const confidencePct = computed(() => Math.round((decision.value?.confidence || 0) * 100))

const checklistEmoji = (status: string) => {
  if (status === 'satisfied') return '✅'
  if (status === 'caution') return '⚠️'
  return '❌'
}

const phaseEmoji = (phase: string) => {
  if (phase === 'pre_market') return '🌅'
  if (phase === 'intraday') return '📊'
  if (phase === 'lunch') return '🍱'
  if (phase === 'near_close') return '🔔'
  if (phase === 'after_hours') return '🌙'
  return '⏰'
}

const displayName = computed(() => {
  const name = decision.value?.stock_name
  if (name && name !== '-' && name !== decision.value?.symbol) return name
  const sym = intent.value?.symbol || decision.value?.symbol
  if (sym && sym !== '-') return sym
  
  // Fallback to query target extraction
  const q = props.report?.query || ''
  if (!q) return '标的'
  const cleaned = q.replace(/^(深度分析|分析|评估|评估一下|看下|深度评估)\s*/, '')
  const match = cleaned.match(/^([a-zA-Z0-9\u4e00-\u9fa5.-]+)/)
  return match ? match[1] : q
})

const displaySymbol = computed(() => {
  const sym = intent.value?.symbol || decision.value?.symbol
  if (sym && sym !== '-') return sym
  
  // Try to match standard symbols in query via regex (e.g. sh.600519 or 600519)
  const q = props.report?.query || ''
  const m = q.match(/\b((?:sh|sz|bj|hk|us)\.[a-z0-9]+)\b/i) || q.match(/\b(\d{6})\b/)
  return m ? m[1].toUpperCase() : '-'
})

const renderedMarkdown = computed(() => {
  const md = props.report?.report_md || ''
  return renderMarkdown(md)
})

// 数据时效与溯源信息
const dataQualityDetail = computed(() => {
  return props.report?.decision?.data_quality_detail || props.report?.data_quality || null
})

// 模拟盘挂单部署 (ALENS-118)
const router = useRouter()
const submittingPaperOrder = ref(false)
const confirmPaperOrderDialog = ref(false)
const deploySuccessDialog = ref(false)
const createdPaperOrder = ref<any>(null)

async function handleDeployPaperOrder() {
  const analysisId = props.report?.analysis_id || props.report?.id
  if (!analysisId) {
    ElMessage.warning('缺少有效分析 ID，无法转为模拟盘订单')
    return
  }
  submittingPaperOrder.value = true
  try {
    const res = await createPaperOrderFromDecision(analysisId)
    createdPaperOrder.value = res
    confirmPaperOrderDialog.value = false
    deploySuccessDialog.value = true
    ElMessage.success('已成功生成模拟盘限价挂单！')
  } catch (e: any) {
    ElMessage.error(`转模拟盘挂单失败: ${e?.response?.data?.detail || e?.message || '未知错误'}`)
  } finally {
    submittingPaperOrder.value = false
  }
}

function goToPaperDeployments() {
  deploySuccessDialog.value = false
  router.push('/paper-deployments')
}

// ===== 存为投资论文档案 (ALENS-194) =====
interface CustomRedline {
  condition: string
  metric_key: string
  operator: RedLineOperator
  threshold: number | null
}

const thesisDrawerVisible = ref(false)
const thesisSubmitting = ref(false)
const createdThesisId = ref<string | null>(null)
const thesisTitle = ref('')
const customRedlines = ref<CustomRedline[]>([])

/** 一键立论抽屉预填: 标题默认 标的名+投资论文; 红线由后端按财务基线自动生成 */
function openThesisDrawer() {
  if (!props.report?.analysis_id) {
    ElMessage.warning('缺少有效分析 ID，无法建立投资论文档案')
    return
  }
  createdThesisId.value = null
  thesisTitle.value = `${displayName.value} 投资论文`
  customRedlines.value = [{ condition: '', metric_key: '', operator: '<', threshold: null }]
  thesisDrawerVisible.value = true
}

function addCustomRedline() {
  customRedlines.value.push({ condition: '', metric_key: '', operator: '<', threshold: null })
}

function removeCustomRedline(index: number) {
  customRedlines.value.splice(index, 1)
}

const thesisAutoRedlines = computed<string[]>(() => {
  const snap = mungerInversion.value?.metrics_snapshot || {}
  const lines: string[] = []
  const stop = decision.value?.sniper_levels?.stop_loss
  if (typeof stop === 'number' && stop > 0) lines.push(`股价跌破物理止损价 ${stop}`)
  lines.push('标的被实施 ST / 退市风险警示 (一票否决)')
  const roe = snap.roe
  if (typeof roe === 'number' && roe > 0) lines.push(`ROE 跌破 ${(roe * 2 / 3).toFixed(1)}% (当前 ${roe}%)`)
  const gm = snap.gross_margin
  if (typeof gm === 'number' && gm > 0) lines.push(`毛利率跌破 ${(gm * 0.75).toFixed(1)}% (当前 ${gm}%)`)
  return lines
})

async function handleCreateThesis() {
  const analysisId = props.report?.analysis_id
  if (!analysisId) return
  thesisSubmitting.value = true
  try {
    const redlines = customRedlines.value
      .filter((r) => r.condition.trim() || (r.metric_key.trim() && r.threshold != null))
      .map((r) => ({
        condition: r.condition.trim() || `${r.metric_key} ${r.operator} ${r.threshold}`,
        ...(r.metric_key.trim() && r.threshold != null
          ? { metric_key: r.metric_key.trim(), operator: r.operator, threshold: r.threshold }
          : {}),
      }))
    const res = await createThesisFromAnalysis(analysisId, {
      thesis_title: thesisTitle.value.trim() || undefined,
      custom_redlines: redlines,
    })
    createdThesisId.value = res.thesis_id
    ElMessage.success('投资论文档案已建立')
  } catch (e: any) {
    ElMessage.error(`建立投资论文失败: ${e?.response?.data?.detail || e?.message || '未知错误'}`)
  } finally {
    thesisSubmitting.value = false
  }
}

function goToThesisWorkbench() {
  thesisDrawerVisible.value = false
  router.push({
    path: '/thesis',
    query: { tab: 'theses', symbol: displaySymbol.value !== '-' ? displaySymbol.value : undefined },
  })
}
</script>

<template>
  <el-card class="report-card" shadow="hover">
    <template #header>
      <div class="report-header">
        <div class="report-title-container">
          <span class="report-tag">投资分析报告</span>
          <h1 class="report-title">
            {{ displayName }}
            <span v-if="displaySymbol && displaySymbol !== '-' && displayName !== displaySymbol" class="report-symbol">({{ displaySymbol }})</span>
          </h1>
        </div>
        <div class="meta-row">
          <span><strong>市场</strong>: {{ marketLabel(decision?.market || intent.market || 'cn') }}</span>
          <span><strong>分析 ID</strong>: <code>{{ report.analysis_id }}</code></span>
          <span><strong>生成时间</strong>: {{ report.created_at || report.completed_at || '-' }}</span>
        </div>
      </div>
    </template>

    <!-- ===== 数据溯源与时效性透明看板 (Data Provenance & Freshness) ===== -->
    <div class="provenance-panel">
      <div class="prov-header">
        <span class="prov-title">📡 数据源溯源与时效性跟踪</span>
        <el-tag size="small" :type="decision?.data_quality === 'good' ? 'success' : decision?.data_quality === 'missing' || decision?.data_quality === 'stale' ? 'danger' : 'warning'" effect="light">
          数据质量: {{ dataQualityLabel(decision?.data_quality) }}
        </el-tag>
      </div>
      <div class="prov-grid">
        <div class="prov-item">
          <div class="prov-item-label">📈 实时行情 / K线</div>
          <div class="prov-item-val">
            <span class="prov-source">TickerLab Pro</span>
            <span class="prov-badge badge-realtime">最新交易日/实时</span>
          </div>
        </div>
        <div class="prov-item">
          <div class="prov-item-label">📑 深度财务与估值</div>
          <div class="prov-item-val">
            <span class="prov-source">TickerLab 官方财报库</span>
            <span class="prov-badge">最新披露期</span>
          </div>
        </div>
        <div class="prov-item">
          <div class="prov-item-label">💬 舆情与市场情绪</div>
          <div class="prov-item-val">
            <span class="prov-source">AlphaScout / 新闻聚合</span>
            <span class="prov-badge">近 7~30 天</span>
          </div>
        </div>
        <div class="prov-item">
          <div class="prov-item-label">🤖 推演与仲裁引擎</div>
          <div class="prov-item-val">
            <span class="prov-source">AlphaLens 多智能体协同</span>
            <span class="prov-badge badge-realtime">Point-in-Time</span>
          </div>
        </div>
      </div>
      <div v-if="dataQualityDetail?.warnings?.length" class="prov-warnings">
        <el-alert
          type="warning"
          :closable="false"
          show-icon
          :title="`数据告警: ${dataQualityDetail.warnings.join('; ')}`"
        />
      </div>
    </div>

    <!-- ===== 🧠 历史自省教训召回 (ALENS-194/180): 本次决策参考的历史教训 ===== -->
    <div v-if="reflections.length" class="reflections-recall">
      <div class="recall-head">
        <h3>🧠 历史自省教训召回 ({{ reflections.length }} 条)</h3>
        <span class="recall-sub">本次终审注入的同标的历史教训与通用投资纪律</span>
      </div>
      <div class="recall-list">
        <div
          v-for="(r, i) in reflections"
          :key="`${r.analysis_id}-${i}`"
          class="recall-item"
          :class="`recall-${r.outcome}`"
        >
          <el-tag :type="r.outcome === 'success' ? 'success' : 'danger'" effect="dark" size="small">
            {{ r.outcome === 'success' ? '成功复盘' : '失败教训' }}
          </el-tag>
          <span class="recall-symbol">{{ r.symbol }}</span>
          <span class="recall-lesson">{{ r.lesson || (r.lessons_learned || [])[0] || '' }}</span>
        </div>
      </div>
    </div>

    <!-- ===== 决策仪表盘: 顶部摘要卡片 ===== -->
    <div v-if="decision" class="dashboard-summary" :style="{ borderColor: actionStyle.color, background: actionStyle.bg }">
      <div class="ds-action">
        <div class="ds-label">操作建议</div>
        <div class="ds-action-text" :style="{ color: actionStyle.color }">{{ actionStyle.label }}</div>
      </div>
      <div class="ds-divider"></div>
      <div class="ds-cell">
        <div class="ds-label">置信度</div>
        <div class="ds-value">
          {{ confidencePct }}%
          <span class="ds-sub">{{ confidenceLabel(decision.confidence_level) }}</span>
        </div>
      </div>
      <div class="ds-cell">
        <div class="ds-label">情绪评分</div>
        <div class="ds-value">{{ (decision.sentiment_score || 0).toFixed(0) }}<span class="ds-sub">/100</span></div>
      </div>
      <div class="ds-cell">
        <div class="ds-label">建议仓位</div>
        <div class="ds-value">{{ decision.sniper_levels.entry ? '狙击建仓' : '观望' }}</div>
      </div>
      <div class="ds-cell">
        <div class="ds-label">数据质量</div>
        <div class="ds-value ds-quality">{{ dataQualityLabel(decision.data_quality) }}</div>
      </div>
    </div>

    <!-- 报告工具栏: 存为投资论文档案 (ALENS-194) -->
    <div class="report-toolbar">
      <el-button
        type="primary"
        size="small"
        plain
        @click="openThesisDrawer"
      >
        📜 存为投资论文档案
      </el-button>
    </div>

    <!-- 核心结论 -->
    <div v-if="decision?.core_conclusion" class="core-conclusion">
      <strong>核心结论</strong>: {{ decision.core_conclusion }}
    </div>

    <!-- 操作建议 -->
    <div v-if="decision?.operation_advice" class="operation-advice">
      <strong>操作建议</strong>: {{ decision.operation_advice }}
    </div>

    <!-- ===== 狙击价位 ===== -->
    <div v-if="decision?.sniper_levels && (decision.sniper_levels.entry || decision.sniper_levels.stop_loss)" class="sniper">
      <div class="sniper-header-bar">
        <h3>🎯 狙击价位</h3>
        <el-button
          v-if="decision?.decision_type === 'buy' || decision?.decision_type === 'sell'"
          type="success"
          size="small"
          :loading="submittingPaperOrder"
          @click="confirmPaperOrderDialog = true"
        >
          🎯 一键转模拟盘挂单
        </el-button>
      </div>
      <el-row :gutter="12">
        <el-col :span="6">
          <div class="sniper-cell sniper-entry">
            <div class="sniper-label">买入</div>
            <div class="sniper-value">{{ decision.sniper_levels.entry ?? '-' }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="sniper-cell sniper-stop">
            <div class="sniper-label">止损</div>
            <div class="sniper-value">{{ decision.sniper_levels.stop_loss ?? '-' }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="sniper-cell sniper-target">
            <div class="sniper-label">目标 1</div>
            <div class="sniper-value">{{ decision.sniper_levels.target_1 ?? '-' }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="sniper-cell sniper-target2">
            <div class="sniper-label">目标 2</div>
            <div class="sniper-value">{{ decision.sniper_levels.target_2 ?? '-' }}</div>
          </div>
        </el-col>
      </el-row>
      <div v-if="decision.sniper_levels.risk_reward_ratio" class="sniper-rr">
        盈亏比: <strong>1:{{ decision.sniper_levels.risk_reward_ratio.toFixed(1) }}</strong>
        <span v-if="decision.sniper_levels.notes" class="sniper-notes"> · {{ decision.sniper_levels.notes }}</span>
      </div>
    </div>

    <!-- ===== ALENS-194: 价值投资双引擎与多智能体结构化面板 ===== -->
    <div class="panel-stack">
      <MungerInversionPanel v-if="mungerInversion" :munger-inversion="mungerInversion" />
      <BuffettChecklistPanel v-if="buffettChecklist" :buffett-checklist="buffettChecklist" />
      <DebateArenaPanel v-if="debateResult" :debate-result="debateResult" />
      <TraderProposalPanel v-if="tradeProposal" :trade-proposal="tradeProposal" />
      <RiskCommitteePanel v-if="riskReviews.length" :risk-reviews="riskReviews" />
      <ValuationBandPanel
        v-if="displaySymbol && displaySymbol !== '-'"
        :initial-symbol="displaySymbol"
      />
    </div>

    <!-- ===== 检查清单 ===== -->
    <div v-if="decision?.checklist?.length" class="checklist">
      <h3>✅ 检查清单</h3>
      <div v-for="item in decision.checklist" :key="item.name" class="checklist-item">
        <span class="checklist-emoji">{{ checklistEmoji(item.status) }}</span>
        <span class="checklist-name">{{ item.name }}</span>
        <span v-if="item.value" class="checklist-value">: {{ item.value }}</span>
        <span v-if="item.detail" class="checklist-detail"> — {{ item.detail }}</span>
      </div>
    </div>

    <!-- ===== 时段决策 ===== -->
    <div v-if="decision?.phase_decision" class="phase-decision">
      <h3>{{ phaseEmoji(decision.phase_decision.phase) }} 时段决策 ({{ phaseLabel(decision.phase_decision.phase) }})</h3>
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="阶段情境">{{ decision.phase_decision.phase_context }}</el-descriptions-item>
        <el-descriptions-item label="行动窗口">{{ decision.phase_decision.action_window }}</el-descriptions-item>
        <el-descriptions-item label="立即行动">
          <el-tag type="primary" effect="dark">{{ decision.phase_decision.immediate_action }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item v-if="decision.phase_decision.watch_conditions?.length" label="观察条件">
          <el-tag v-for="(c, i) in decision.phase_decision.watch_conditions" :key="i" type="info" size="small" style="margin-right: 6px;">
            {{ c }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="置信度理由">{{ decision.phase_decision.confidence_reason }}</el-descriptions-item>
        <el-descriptions-item v-if="decision.phase_decision.data_limitations?.length" label="数据局限">
          <el-text type="warning" size="small">{{ decision.phase_decision.data_limitations.join('; ') }}</el-text>
        </el-descriptions-item>
      </el-descriptions>
    </div>

    <!-- ===== 关键观点 ===== -->
    <div v-if="decision?.key_points?.length" class="key-points">
      <h3>📌 关键观点</h3>
      <ul>
        <li v-for="(kp, i) in decision.key_points" :key="i">{{ kp }}</li>
      </ul>
    </div>

    <!-- ===== 风险提示 ===== -->
    <div v-if="decision?.risk_warning?.length" class="risk-warning">
      <h3>⚠️ 风险提示</h3>
      <ul>
        <li v-for="(r, i) in decision.risk_warning" :key="i">{{ r }}</li>
      </ul>
    </div>

    <!-- ===== 触发的纪律规则 ===== -->
    <div v-if="decision?.triggered_rules?.length" class="triggered-rules">
      <el-alert
        v-for="(rule, i) in decision.triggered_rules"
        :key="i"
        :title="rule"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 6px;"
      />
    </div>

    <el-divider />

    <!-- ===== 完整 Markdown 报告 ===== -->
    <h3>📄 详细报告</h3>
    <div class="markdown-body" v-html="renderedMarkdown"></div>

    <!-- 模拟盘挂单确认弹窗 (ALENS-118) -->
    <el-dialog
      v-model="confirmPaperOrderDialog"
      title="确认转为模拟盘挂单"
      width="480px"
      append-to-body
    >
      <div class="dialog-order-preview">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="交易标的">
            <b>{{ displayName }} ({{ displaySymbol }})</b>
          </el-descriptions-item>
          <el-descriptions-item label="交易方向">
            <el-tag :type="decision?.decision_type === 'buy' ? 'success' : 'danger'" size="small">
              {{ decision?.decision_type?.toUpperCase() }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="挂单买入/入场限价">
            <b>{{ decision?.sniper_levels?.entry ?? '-' }} 元</b>
          </el-descriptions-item>
          <el-descriptions-item label="止损价">
            <span style="color: #ea4335;">{{ decision?.sniper_levels?.stop_loss ?? '-' }} 元</span>
          </el-descriptions-item>
          <el-descriptions-item label="第一目标价">
            <span style="color: #34a853;">{{ decision?.sniper_levels?.target_1 ?? '-' }} 元</span>
          </el-descriptions-item>
        </el-descriptions>
        <div class="order-rule-tip">
          💡 <b>交易规则提示</b>: 订单将以限价单形式进入您的模拟盘账户，并在交易时段按真实行情进行撮合成交，严格遵循 A股 T+1 交易制度与费率体系。
        </div>
      </div>
      <template #footer>
        <el-button @click="confirmPaperOrderDialog = false">取消</el-button>
        <el-button type="primary" :loading="submittingPaperOrder" @click="handleDeployPaperOrder">
          确认挂单
        </el-button>
      </template>
    </el-dialog>

    <!-- 模拟盘挂单成功弹窗 (ALENS-118) -->
    <el-dialog
      v-model="deploySuccessDialog"
      title="模拟盘挂单成功"
      width="420px"
      append-to-body
    >
      <div style="text-align: center; padding: 12px 0;">
        <div style="font-size: 36px; margin-bottom: 8px;">🎉</div>
        <p style="font-size: 15px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px;">
          订单已提交至模拟盘挂单队列
        </p>
        <p style="font-size: 12px; color: var(--text-secondary);">
          订单 ID: <code>{{ createdPaperOrder?.order_id || createdPaperOrder?.id || '-' }}</code>
        </p>
      </div>
      <template #footer>
        <el-button @click="deploySuccessDialog = false">留在本页</el-button>
        <el-button type="success" @click="goToPaperDeployments">前往模拟盘看板查看</el-button>
      </template>
    </el-dialog>

    <!-- 存为投资论文档案抽屉 (ALENS-194) -->
    <el-drawer
      v-model="thesisDrawerVisible"
      title="📜 存为投资论文档案 (Thesis Archive)"
      size="520px"
      append-to-body
    >
      <div v-if="!createdThesisId" class="thesis-form">
        <el-form label-position="top" size="default">
          <el-form-item label="档案标题">
            <el-input v-model="thesisTitle" maxlength="80" show-word-limit placeholder="投资论文标题" />
          </el-form-item>
        </el-form>

        <div class="thesis-preview">
          <div class="preview-title">自动提取内容预览</div>
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item label="标的">
              {{ displayName }} ({{ displaySymbol }})
            </el-descriptions-item>
            <el-descriptions-item label="买入核心逻辑">
              {{ decision?.core_conclusion || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="催化剂清单">
              {{ decision?.key_points?.length ? decision.key_points.join('；') : '-' }}
            </el-descriptions-item>
          </el-descriptions>
          <div class="preview-title" style="margin-top: 10px;">默认证伪红线 (按当前财务基线自动生成)</div>
          <ul class="auto-redlines">
            <li v-for="(r, i) in thesisAutoRedlines" :key="i">{{ r }}</li>
          </ul>
        </div>

        <div class="preview-title" style="margin: 12px 0 6px;">自定义补充红线 (可选)</div>
        <div v-for="(row, idx) in customRedlines" :key="idx" class="redline-row">
          <el-input
            v-model="row.condition"
            placeholder="红线描述, 如: 核心管理层变更"
            size="small"
            style="flex: 1;"
          />
          <el-input v-model="row.metric_key" placeholder="指标键(选填)" size="small" style="width: 110px;" />
          <el-select v-model="row.operator" size="small" style="width: 70px;">
            <el-option label="<" value="<" />
            <el-option label="<=" value="<=" />
            <el-option label=">" value=">" />
            <el-option label=">=" value=">=" />
          </el-select>
          <el-input-number v-model="row.threshold" :controls="false" placeholder="阈值" size="small" style="width: 90px;" />
          <el-button size="small" type="danger" plain circle @click="removeCustomRedline(idx)">✕</el-button>
        </div>
        <el-button size="small" text type="primary" @click="addCustomRedline">+ 添加红线</el-button>
      </div>

      <div v-else class="thesis-success">
        <div class="success-emoji">🎉</div>
        <p class="success-text">投资论文档案已建立</p>
        <p class="success-id">档案 ID: <code>{{ createdThesisId }}</code></p>
      </div>

      <template #footer>
        <template v-if="!createdThesisId">
          <el-button @click="thesisDrawerVisible = false">取消</el-button>
          <el-button type="primary" :loading="thesisSubmitting" @click="handleCreateThesis">
            确认建档
          </el-button>
        </template>
        <template v-else>
          <el-button @click="thesisDrawerVisible = false">留在本页</el-button>
          <el-button type="success" @click="goToThesisWorkbench">前往价值投资工作台</el-button>
        </template>
      </template>
    </el-drawer>
  </el-card>
</template>

<style scoped>
.report-card { margin-bottom: 16px; }
.report-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.report-title-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-tag {
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
  letter-spacing: 1px;
  text-transform: uppercase;
}
.report-title {
  font-size: 24px;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.report-symbol {
  font-size: 16px;
  font-weight: 400;
  color: var(--text-secondary);
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: var(--text-secondary);
  border-top: 1px solid var(--border-color);
  padding-top: 8px;
  margin-top: 4px;
}
.meta-row code {
  background: var(--code-bg);
  color: var(--code-color);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

/* Provenance Panel */
.provenance-panel {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.prov-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.prov-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.prov-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}
.prov-item {
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.prov-item-label {
  font-size: 11px;
  color: var(--text-muted);
}
.prov-item-val {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.prov-source {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}
.prov-badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}
.badge-realtime {
  background: var(--bg-success-subtle);
  color: var(--success);
  border-color: transparent;
}
.prov-warnings {
  margin-top: 10px;
}

.dashboard-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border: 2px solid;
  border-radius: 8px;
  margin-bottom: 16px;
  transition: all 0.2s ease;
}
.ds-action { min-width: 100px; }
.ds-label { font-size: 12px; color: var(--text-secondary); opacity: 0.9; margin-bottom: 4px; }
.ds-action-text { font-size: 22px; font-weight: 700; }
.ds-divider { width: 1px; height: 36px; background: var(--border-color); opacity: 0.6; }
.ds-cell { flex: 1; text-align: center; }
.ds-value { font-size: 20px; font-weight: 700; color: var(--text-primary); }
.ds-sub { font-size: 12px; color: var(--text-secondary); margin-left: 4px; font-weight: normal; }
.ds-quality { font-size: 14px; text-transform: uppercase; font-family: monospace; }

.core-conclusion, .operation-advice {
  font-size: 15px; padding: 10px 14px;
  background: var(--accent-subtle); border-left: 3px solid var(--accent);
  color: var(--text-primary);
  margin-bottom: 10px; line-height: 1.6;
  border-radius: 0 6px 6px 0;
}

.sniper h3, .checklist h3, .phase-decision h3, .key-points h3, .risk-warning h3 {
  color: var(--text-primary); font-size: 16px; margin: 16px 0 10px; font-weight: 600;
}
.sniper-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.sniper-header-bar h3 {
  margin: 0;
}
.dialog-order-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.order-rule-tip {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  padding: 10px 12px;
  border-radius: 6px;
  line-height: 1.5;
}
.sniper-cell {
  text-align: center; padding: 14px; border-radius: 6px; color: #ffffff;
}
.sniper-entry { background: var(--accent); }
.sniper-stop { background: var(--danger); }
.sniper-target { background: var(--success); }
.sniper-target2 { background: var(--success); filter: brightness(0.85); }
.sniper-label { font-size: 12px; opacity: 0.9; }
.sniper-value { font-size: 22px; font-weight: 700; margin-top: 4px; }
.sniper-rr { margin-top: 10px; font-size: 14px; color: var(--text-primary); }
.sniper-notes { color: var(--text-secondary); font-size: 13px; }

.checklist-item { padding: 6px 0; font-size: 14px; color: var(--text-primary); }
.checklist-emoji { font-size: 16px; margin-right: 6px; }
.checklist-name { font-weight: 600; }
.checklist-value { color: var(--text-secondary); }
.checklist-detail { color: var(--text-muted); font-size: 13px; }

.key-points ul, .risk-warning ul { padding-left: 20px; line-height: 1.8; color: var(--text-primary); }

.markdown-body { line-height: 1.75; font-size: 14px; color: var(--text-primary); }

/* ===== ALENS-194: 反思召回 / 面板栈 / 论文抽屉 ===== */
.reflections-recall {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--warning);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.recall-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.recall-head h3 { margin: 0; font-size: 15px; font-weight: 700; color: var(--text-primary); }
.recall-sub { font-size: 12px; color: var(--text-muted); }
.recall-list { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.recall-item {
  display: flex; align-items: flex-start; gap: 8px; font-size: 13px; line-height: 1.6;
}
.recall-symbol { font-weight: 700; color: var(--text-primary); flex-shrink: 0; }
.recall-lesson { color: var(--text-secondary); }
.report-toolbar { display: flex; justify-content: flex-end; margin-bottom: 10px; }
.panel-stack { display: flex; flex-direction: column; gap: 14px; margin: 14px 0; }
.thesis-form { display: flex; flex-direction: column; }
.thesis-preview {
  background: var(--bg-tertiary); border-radius: 8px; padding: 12px;
}
.preview-title { font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
.auto-redlines { padding-left: 18px; margin: 0; font-size: 12px; color: var(--text-secondary); line-height: 1.8; }
.redline-row { display: flex; gap: 6px; align-items: center; margin-bottom: 8px; }
.thesis-success { text-align: center; padding: 32px 0; }
.success-emoji { font-size: 36px; margin-bottom: 8px; }
.success-text { font-size: 15px; font-weight: 600; color: var(--text-primary); margin: 0 0 6px; }
.success-id { font-size: 12px; color: var(--text-secondary); }
.success-id code { background: var(--code-bg); color: var(--code-color); padding: 2px 6px; border-radius: 4px; }
.markdown-body :deep(h1) { color: var(--accent); border-bottom: 2px solid var(--border-color); padding-bottom: 8px; }
.markdown-body :deep(h2) { color: var(--success); margin-top: 24px; border-left: 4px solid var(--success); padding-left: 12px; }
.markdown-body :deep(h3) { color: var(--warning); }
.markdown-body :deep(blockquote) { background: var(--blockquote-bg); border-left: 4px solid var(--blockquote-border); padding: 8px 16px; color: var(--text-secondary); }
.markdown-body :deep(img) { max-width: 100%; border: 1px solid var(--border-color); border-radius: 4px; }
.markdown-body :deep(code) { background: var(--code-bg); color: var(--code-color); padding: 2px 6px; border-radius: 3px; }
</style>